/**
 * API Error Handler Utility
 * 
 * Provides standardized error handling for API responses
 */
import { extractError } from './responseHandler';
import toast from 'react-hot-toast';

/**
 * Handle API errors and return standardized error object
 * Also shows user-friendly toast notification
 * @param {Error} error - Axios error object
 * @param {string} context - Context for logging (e.g., 'TicketDetail')
 * @returns {object} Error object with message, type, and status
 */
export const handleApiError = (error, context = '') => {
  const errorInfo = extractError(error);
  
  // Log for debugging
  console.error(`[${context}] API Error:`, errorInfo);
  
  // Show user-friendly message
  const userMessage = getUserFriendlyMessage(errorInfo);
  toast.error(userMessage);
  
  // Return error info for component handling
  return {
    ...errorInfo,
    type: getErrorType(errorInfo.status)
  };
};

/**
 * Extract clean, user-friendly error message from error object
 * Handles JSON strings, technical exception details, and various error formats
 * @param {Error|object} error - Error object from API or catch block
 * @param {string} defaultMessage - Default message if extraction fails
 * @returns {string} Clean, user-friendly error message
 */
export const extractErrorMessage = (error, defaultMessage = 'An error occurred. Please try again.') => {
  try {
    // If error has response.data.message (axios-style)
    if (error?.response?.data?.message) {
      const msg = error.response.data.message;
      // Check if it's a JSON string and parse it
      if (typeof msg === 'string' && msg.trim().startsWith('{')) {
        try {
          const parsed = JSON.parse(msg);
          return cleanErrorMessage(parsed.message || parsed.error || defaultMessage);
        } catch {
          return cleanErrorMessage(msg);
        }
      }
      return cleanErrorMessage(msg);
    }

    // If error has response.data.error
    if (error?.response?.data?.error) {
      return cleanErrorMessage(error.response.data.error);
    }

    // If error.response.data is a JSON string
    if (error?.response?.data && typeof error.response.data === 'string' && error.response.data.trim().startsWith('{')) {
      try {
        const parsed = JSON.parse(error.response.data);
        return cleanErrorMessage(parsed.message || parsed.error || defaultMessage);
      } catch {
        return cleanErrorMessage(error.response.data);
      }
    }

    // If error.message is a JSON string, try to parse it
    if (error?.message) {
      const errorMsg = error.message;
      if (typeof errorMsg === 'string' && errorMsg.trim().startsWith('{')) {
        try {
          const parsed = JSON.parse(errorMsg);
          // Extract message from parsed JSON
          if (parsed.message) {
            return cleanErrorMessage(parsed.message);
          }
          return cleanErrorMessage(parsed.error || defaultMessage);
        } catch {
          // If parsing fails, return the message as is but clean it up
          return cleanErrorMessage(errorMsg);
        }
      }
      // Clean up technical error messages
      return cleanErrorMessage(errorMsg);
    }

    return defaultMessage;
  } catch (err) {
    console.error('Error extracting error message:', err);
    return defaultMessage;
  }
};

/**
 * Clean error message by removing technical details
 * @param {string} message - Raw error message
 * @returns {string} Cleaned error message
 */
const cleanErrorMessage = (message) => {
  if (!message || typeof message !== 'string') {
    return 'An error occurred. Please try again.';
  }

  let cleanMessage = message;
  
  // Remove exception class names (e.g., "MethodArgumentTypeMismatchException: ")
  cleanMessage = cleanMessage.replace(/^[A-Za-z0-9_$.]+(?:Exception|Error):\s*/i, '');
  
  // Remove "Method parameter 'X': " prefix
  cleanMessage = cleanMessage.replace(/Method\s+parameter\s+['"][^'"]+['"]:\s*/gi, '');
  
  // Remove "For input string: "X"" details
  cleanMessage = cleanMessage.replace(/For\s+input\s+string:\s*["'][^"']+["']/gi, '');
  
  // Remove "Failed to convert value of type" technical details
  cleanMessage = cleanMessage.replace(/Failed\s+to\s+convert\s+value\s+of\s+type\s+[^;]+;\s*/gi, '');
  
  // Remove "to required type" technical details
  cleanMessage = cleanMessage.replace(/to\s+required\s+type\s+[^;]+[;.]?\s*/gi, '');
  
  // Remove "An unexpected error occurred: " prefix if it's followed by technical details
  cleanMessage = cleanMessage.replace(/^An\s+unexpected\s+error\s+occurred:\s*/i, '');
  
  // Clean up multiple spaces and trim
  cleanMessage = cleanMessage.replace(/\s+/g, ' ').trim();
  
  // If message is empty or too technical, return default
  if (!cleanMessage || cleanMessage.length < 3) {
    return 'An error occurred. Please try again.';
  }
  
  // Capitalize first letter
  cleanMessage = cleanMessage.charAt(0).toUpperCase() + cleanMessage.slice(1);
  
  // Ensure it ends with proper punctuation
  if (!cleanMessage.match(/[.!?]$/)) {
    cleanMessage += '.';
  }
  
  return cleanMessage;
};

/**
 * Get user-friendly error message based on error info
 * @param {object} errorInfo - Error info from extractError
 * @returns {string} User-friendly error message
 */
const getUserFriendlyMessage = (errorInfo) => {
  // First try to extract and clean the message
  const extractedMessage = extractErrorMessage(errorInfo, null);
  if (extractedMessage && extractedMessage !== 'An error occurred. Please try again.') {
    return extractedMessage;
  }

  // Fallback to status-based messages
  switch (errorInfo.status) {
    case 400:
      return errorInfo.fields 
        ? 'Please check the form for errors'
        : errorInfo.message || 'Invalid request. Please check your input.';
    case 401:
      return 'Session expired. Please log in again.';
    case 403:
      return 'You do not have permission to perform this action.';
    case 404:
      return 'The requested resource was not found.';
    case 500:
      return 'Server error. Please try again later or contact support.';
    default:
      return errorInfo.message || 'An unexpected error occurred. Please try again.';
  }
};

/**
 * Get error type from status code
 * @param {number} status - HTTP status code
 * @returns {string} Error type
 */
const getErrorType = (status) => {
  switch (status) {
    case 400:
    case 422:
      return 'validation';
    case 401:
      return 'auth';
    case 403:
      return 'permission';
    case 404:
      return 'notFound';
    case 500:
    case 502:
    case 503:
      return 'server';
    case 429:
      return 'rateLimit';
    default:
      return 'unknown';
  }
};

/**
 * Retry API call with exponential backoff
 * @param {Function} apiCall - Function that returns a promise
 * @param {number} maxRetries - Maximum number of retries (default: 3)
 * @returns {Promise} Result of API call
 */
export const retryApiCall = async (apiCall, maxRetries = 3) => {
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await apiCall();
    } catch (error) {
      if (i === maxRetries - 1) throw error;
      // Wait before retry (exponential backoff)
      await new Promise(resolve => setTimeout(resolve, Math.pow(2, i) * 1000));
    }
  }
};

/**
 * Handle API errors (legacy function for backward compatibility)
 * @param {Error} error - Axios error object
 * @returns {object} Error object with message and type
 */
export const handleApiErrorLegacy = (error) => {
  if (error.response) {
    // Server responded with error status
    const status = error.response.status;
    const message = error.response.data?.message || error.response.data?.error || 'An error occurred';
    
    switch (status) {
      case 400:
        return { message, type: 'validation', status };
      case 401:
        return { message: 'Unauthorized. Please login again.', type: 'auth', status };
      case 403:
        return { message: 'Access denied.', type: 'permission', status };
      case 404:
        return { message: 'Resource not found.', type: 'notFound', status };
      case 409:
        return { message: message || 'Conflict. Resource already exists.', type: 'conflict', status };
      case 422:
        return { message, type: 'validation', status };
      case 429:
        return { message: 'Too many requests. Please try again later.', type: 'rateLimit', status };
      case 500:
        return { message: 'Server error. Please try again later.', type: 'server', status };
      case 502:
        return { message: 'Bad gateway. Service temporarily unavailable.', type: 'server', status };
      case 503:
        return { message: 'Service unavailable. Please try again later.', type: 'server', status };
      default:
        return { message, type: 'unknown', status };
    }
  } else if (error.request) {
    // Request made but no response
    return { 
      message: 'Network error. Please check your connection.', 
      type: 'network', 
      status: null 
    };
  } else {
    // Something else happened
    return { 
      message: error.message || 'An unexpected error occurred', 
      type: 'unknown', 
      status: null 
    };
  }
};

/**
 * Check if error is a specific type
 * @param {Error} error - Error object
 * @param {string} type - Error type to check
 * @returns {boolean} True if error matches type
 */
export const isErrorType = (error, type) => {
  const errorInfo = handleApiError(error);
  return errorInfo.type === type;
};

/**
 * Get user-friendly error message
 * @param {Error} error - Error object
 * @returns {string} User-friendly error message
 */
export const getErrorMessage = (error) => {
  return extractErrorMessage(error, 'An unexpected error occurred. Please try again.');
};

/**
 * Check if error requires authentication
 * @param {Error} error - Error object
 * @returns {boolean} True if error is 401
 */
export const isAuthError = (error) => {
  return isErrorType(error, 'auth');
};

/**
 * Check if error is a validation error
 * @param {Error} error - Error object
 * @returns {boolean} True if error is validation-related
 */
export const isValidationError = (error) => {
  return isErrorType(error, 'validation');
};

export default {
  handleApiError,
  handleApiErrorLegacy,
  isErrorType,
  getErrorMessage,
  extractErrorMessage,
  isAuthError,
  isValidationError,
  retryApiCall,
};

